import { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/Input";
import CryptoJS from "crypto-js";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, RefreshCw, Code, ShieldAlert } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

export function PkceGenerator() {
  const [length, setLength] = useState(64);
  const [codeVerifier, setCodeVerifier] = useState("");
  const [codeChallenge, setCodeChallenge] = useState("");
  const [copiedVerifier, setCopiedVerifier] = useState(false);
  const [copiedChallenge, setCopiedChallenge] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  // Helper to convert WordArray to Base64URL
  const base64url = (source: CryptoJS.lib.WordArray): string => {
    let encoded = CryptoJS.enc.Base64.stringify(source);
    return encoded.replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
  };

  const generatePkce = () => {
    // Generate secure random string for Code Verifier
    const allowed = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    let verifier = "";
    
    // Using standard secure-enough randomized picker
    for (let i = 0; i < length; i++) {
      verifier += allowed.charAt(Math.floor(Math.random() * allowed.length));
    }
    
    setCodeVerifier(verifier);

    // Compute SHA-256 Hash of verifier
    const hash = CryptoJS.SHA256(verifier);
    // Base64URL encode the hash
    const challenge = base64url(hash);
    setCodeChallenge(challenge);
  };

  useEffect(() => {
    generatePkce();
  }, [length]);

  const handleCopyVerifier = () => {
    copyToClipboard(codeVerifier).then(() => {
      setCopiedVerifier(true);
      setTimeout(() => setCopiedVerifier(false), 2000);
    });
  };

  const handleCopyChallenge = () => {
    copyToClipboard(codeChallenge).then(() => {
      setCopiedChallenge(true);
      setTimeout(() => setCopiedChallenge(false), 2000);
    });
  };

  const handleCopyAll = () => {
    const formatted = JSON.stringify({
      code_verifier: codeVerifier,
      code_challenge: codeChallenge,
      code_challenge_method: "S256"
    }, null, 2);
    
    copyToClipboard(formatted).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    });
  };

  // Code snippets in different languages
  const snippets = {
    js: `// JavaScript/TypeScript PKCE Generation
import CryptoJS from "crypto-js";

function base64url(source) {
  let encoded = CryptoJS.enc.Base64.stringify(source);
  return encoded.replace(/=+$/, "").replace(/\\+/g, "-").replace(/\\//g, "_");
}

function generatePKCE() {
  // 1. Generate random Code Verifier (43-128 chars)
  const allowed = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  let verifier = "";
  for (let i = 0; i < 64; i++) {
    verifier += allowed.charAt(Math.floor(Math.random() * allowed.length));
  }

  // 2. Compute SHA-256 Code Challenge
  const hash = CryptoJS.SHA256(verifier);
  const challenge = base64url(hash);

  return { verifier, challenge, method: "S256" };
}`,
    python: `# Python PKCE Generation
import hashlib
import base64
import secrets

def generate_pkce():
    # 1. Generate random Code Verifier
    allowed = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~"
    verifier = "".join(secrets.choice(allowed) for _ in range(64))
    
    # 2. Compute SHA-256 Code Challenge
    sha_hash = hashlib.sha256(verifier.encode('utf-8')).digest()
    challenge = base64.urlsafe_b64encode(sha_hash).decode('utf-8').replace('=', '')
    
    return {
        "verifier": verifier,
        "challenge": challenge,
        "method": "S256"
    }`
  };

  return (
    <ToolPageLayout
      title="PKCE Generator Online - Proof Key for Code Exchange"
      description="Securely generate OAuth 2.0 PKCE Code Verifiers and SHA-256 Code Challenges for SPA, mobile, and desktop applications."
      h1="PKCE Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Adjust the slider or number input to set your desired Code Verifier length (usually 43-128).",
        "The Code Verifier and S256 Code Challenge are instantly generated in your local browser memory.",
        "Copy individual elements or copy them together as a JSON structure.",
        "Integrate generated routines into your code using copyable snippets below."
      ]}
      faq={[
        { question: "What is PKCE (Proof Key for Code Exchange) and why is it used?", answer: "PKCE (Proof Key for Code Exchange, pronounced 'pixie') is a secure extension to the OAuth 2.0 Authorization Code flow. It is designed to prevent authorization code interception attacks on public clients—such as Single Page Applications (SPAs), mobile, and native desktop applications—which cannot securely store client secrets." },
        { question: "What is a PKCE Code Verifier?", answer: "A PKCE Code Verifier is a high-entropy cryptographic random string using unreserved URL-safe characters (A-Z, a-z, 0-9, '-', '.', '_', '~'). It must have a minimum length of 43 characters and a maximum length of 128 characters to ensure protection against brute-force guessing." },
        { question: "What is a PKCE Code Challenge and how is it generated?", answer: "A PKCE Code Challenge is the derived value of the Code Verifier. In the secure 'S256' method, it is created by hashing the Code Verifier string with SHA-256 and then encoding the resulting binary digest using Base64URL without padding characters." },
        { question: "What is the difference between the 'plain' and 'S256' PKCE challenge methods?", answer: "The 'plain' method sets the Code Challenge equal to the Code Verifier. The 'S256' method hashes the verifier with SHA-256. 'S256' is highly recommended because even if the challenge is intercepted over the wire, attackers cannot reverse-engineer the original verifier." },
        { question: "How does PKCE protect single-page and mobile apps from interception?", answer: "In public clients, standard client secrets cannot be kept secure from decompilation or browser inspection. With PKCE, a dynamic, single-use secret (the Code Verifier) is generated on the fly. An attacker intercepting the redirect cannot exchange the code because they lack this verifier." },
        { question: "Is a static Client Secret still necessary when utilizing PKCE?", answer: "For public clients (like SPAs and mobile apps), a client secret is not used. For confidential clients (like traditional backend apps), combining a client secret with PKCE is a best practice recommended by OAuth 2.1 to provide defense-in-depth protection." },
        { question: "What is the recommended character length for a secure Code Verifier?", answer: "The OAuth 2.0 PKCE specification (RFC 7636) states the verifier must be between 43 and 128 characters. Using the maximum length of 128 characters is recommended to achieve the highest cryptographic entropy." },
        { question: "Does PKCE replace the OAuth 2.0 'state' parameter?", answer: "No. While both add security, they target different attack vectors. PKCE prevents code interception on the redirection back to the client, while the 'state' parameter prevents Cross-Site Request Forgery (CSRF) on the authorization request." },
        { question: "How does the OAuth server validate the PKCE handshake?", answer: "1. The client sends the code_challenge to the /authorize endpoint. 2. The server saves it. 3. The client sends the code_verifier to the /token endpoint. 4. The server hashes the verifier and ensures it matches the stored challenge. If yes, it issues the token." },
        { question: "Can PKCE be used with OpenID Connect (OIDC)?", answer: "Yes, PKCE integrates perfectly with OpenID Connect. It secures the authorization code flow used to retrieve both the OAuth 2.0 Access Token and the OIDC ID Token on client devices." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Settings Console */}
          <div className="space-y-4 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 text-rose-600">
                <ShieldAlert className="w-5 h-5" />
                <span className="font-bold text-sm uppercase tracking-wider">PKCE Parameter Setup</span>
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700">Code Verifier Length</Label>
                <div className="flex items-center gap-4 mt-2">
                  <input
                    type="range"
                    min="43"
                    max="128"
                    value={length}
                    onChange={(e) => setLength(Number(e.target.value))}
                    className="flex-1 accent-rose-600 h-2 bg-slate-200 rounded-lg cursor-pointer"
                  />
                  <Input
                    type="number"
                    min="43"
                    max="128"
                    value={length}
                    onChange={(e) => setLength(Math.max(43, Math.min(128, Number(e.target.value))))}
                    className="w-20 text-center font-semibold"
                  />
                </div>
                <span className="text-xs text-slate-400 block mt-1">Recommended length is 43 to 128 characters.</span>
              </div>
            </div>

            <Button
              onClick={generatePkce}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white gap-2 mt-4"
            >
              <RefreshCw className="w-4 h-4" />
              Generate PKCE Pair
            </Button>
          </div>

          {/* Results Console */}
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code Verifier (Client Secret)</Label>
                <button
                  onClick={handleCopyVerifier}
                  className="text-xs text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copiedVerifier ? "Copied!" : "Copy"}
                </button>
              </div>
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs select-all break-all text-slate-800 leading-relaxed min-h-[50px] flex items-center">
                {codeVerifier}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Code Challenge (S256)</Label>
                <button
                  onClick={handleCopyChallenge}
                  className="text-xs text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copiedChallenge ? "Copied!" : "Copy"}
                </button>
              </div>
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs select-all break-all text-slate-800 leading-relaxed min-h-[50px] flex items-center">
                {codeChallenge}
              </div>
            </div>

            <Button
              variant="outline"
              onClick={handleCopyAll}
              className="w-full text-xs font-medium gap-2 border-slate-300"
            >
              <Copy className="w-4 h-4" />
              {copiedAll ? "Copied JSON!" : "Copy as OAuth Parameter JSON"}
            </Button>
          </div>
        </div>

        {/* Code Integration Guide */}
        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-slate-800 border-b border-slate-200 pb-2">
            <Code className="w-5 h-5" />
            <h4 className="font-bold text-sm uppercase tracking-wider">Implementation Guide</h4>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">JavaScript/Node.js</span>
              <pre className="p-3 bg-white border border-slate-200 rounded-xl text-[10px] font-mono text-slate-700 overflow-x-auto max-h-[220px]">
                {snippets.js}
              </pre>
            </div>
            <div className="space-y-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Python</span>
              <pre className="p-3 bg-white border border-slate-200 rounded-xl text-[10px] font-mono text-slate-700 overflow-x-auto max-h-[220px]">
                {snippets.python}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
